import {
    VanillaMouseEventCallback,
    VanillaPointerEventCallback, VanillaReadingRulerDragCallback, VanillaReadingRulerMoveCallback,
    VanillaVoidCallback
} from "../VanillaReader/VanillaReaderModel";
import {ICoordinates} from "../utils/ICoordinates";
import {IVanillaReaderUiReadingRulerOptions} from "./VanillaReaderUiModel";
import {VanillaReaderAppUiDefaults} from "./VanillaReaderAppUiDefaults";
import {IRect} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {
    IVisibleContentRectData,
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";

/**
 * # VanillaReaderUiReadingRuler
 *
 * ## RESPONSIBILITIES
 *
 * The reading ruler is a UI overlay that aims to make it easier for people with attention deficit disorders and
 * dyslexia to focus on a smaller range of content at a time.
 *
 * It works by overlaying a coloured box on top of the publication that spans a number of lines content. To work it needs
 * to know the rectangles representing the text and elements in the visible content. This data comes in the form of
 * `IVisibleContentRectData` that is provided by the Colibrio framework and exposed to the UI in various events whenever
 * the visible content changes.
 *
 * The ruler can optionally obscure the publication content that is not covered by the ruler. This is done using a CSS
 * clip path that is moved along with the ruler. The CSS clip-path position is updated using CSS variables named
 * `--vanilla-reader__ruler__overlay__ruler-clip-box-*`.
 *
 * The ruler can be controlled by either dragging or, if set up to do so in the implementing code, by keyboard and mouse and touch events.
 *
 * ### IMPLEMENTATION NOTES
 *
 * I consider this very much a Beta still since there have not been enough user feedback yet.
 *
 * This implementation uses CSS transitions. This comes with some async headaches that are overcome by using `setTimeout` in
 * all places where animations are triggered. I am considering moving over to javascript based animation instead.
 *
 * ## Notable public methods
 * - `setContentLineBoxes()` -
 *      updates the reading rulers list of visible content to snap to. Needs to called everytime new data is available from the `VanillaReader` module.
 * - `moveToAndAlign()` -
 *      Moves the ruler and aligns it to the intersecting content line boxes.
 * - `moveTo()` -
 *      Moves the ruler without aligning it to the intersecting content line boxes. Used while dragging for example.
 * - `setOptions()` -
 *      Applies the supplied `IVanillaReaderUiReadingRulerOptions` options. There are options for many of the visual aspects
 *      of the ruler. Many are still untested, but theoretically should work.
 *
 * ## RELATED TYPES
 * - VanillaReaderUiDialogSettingsController
 * - IVisibleContentRectData
 *
 */
export class VanillaReaderUiReadingRuler {
    private _style = `
        <style>            
        :root {
              --vanilla-reader__ruler__overlay__ruler-clip-box-top: 0;
              --vanilla-reader__ruler__overlay__ruler-clip-box-left: 0;
              --vanilla-reader__ruler__overlay__ruler-clip-box-bottom: 0;
              --vanilla-reader__ruler__overlay__ruler-clip-box-right: 0;            

        }
            #vanilla-reader__ruler__overlay {            
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                touch-action: none;
                background-color: transparent;
                mix-blend-mode: multiply;                
                z-index: 1;
                clip-path: polygon( evenodd,
                    /* overlay rect */
                    0 0,
                    100% 0,
                    100% 100%,
                    0% 100%,
                    0 0,
                    /* ruler clip rect */
                    var(--vanilla-reader__ruler__overlay__ruler-clip-box-left) var(--vanilla-reader__ruler__overlay__ruler-clip-box-top),
                    var(--vanilla-reader__ruler__overlay__ruler-clip-box-right) var(--vanilla-reader__ruler__overlay__ruler-clip-box-top),
                    var(--vanilla-reader__ruler__overlay__ruler-clip-box-right) var(--vanilla-reader__ruler__overlay__ruler-clip-box-bottom),
                    var(--vanilla-reader__ruler__overlay__ruler-clip-box-left) var(--vanilla-reader__ruler__overlay__ruler-clip-box-bottom),
                    var(--vanilla-reader__ruler__overlay__ruler-clip-box-left) var(--vanilla-reader__ruler__overlay__ruler-clip-box-top)             
                );
                opacity: 0;                
            }
        
            #vanilla-reader__ruler {
                top: 0;
                left: 0;
                width: 100%;                
                z-index: 1;                
                position: fixed;
                display: none;
                flex-direction: row;
                align-items: center;
                justify-content:center;                      
                mix-blend-mode: multiply;
                touch-action: none;
                opacity: 0;     
                box-shadow: 0 0 16px rgb(0 0 0 / 8%);                  
            }
            
            #vanilla-reader__ruler__linebox {
                flex-grow: 8;                
                mix-blend-mode: initial;
                border: 0;
                border-inline: none;
                pointer-events: none;
            }
            
            #vanilla-reader__ruler__drag-handle-start {
                flex-grow: 1;
            }
    
            #vanilla-reader__ruler__drag-handle-end {
                flex-grow: 1;                                
            }
            
            .vanilla-reader__ruler__drag-handle {
                display: none;
                outline: 2px solid rgb(0 0 0 / 25%);
                height: 64px;
                width: 64px;
                min-width: 64px;
                border-radius: 4px;
            }
            
            .isBeingDragged {
                box-shadow: 0 4px 16px rgb(0 0 0 / 16%) !important;             
            }
        </style>
    `;
    private _template = `
        <div id="vanilla-reader__ruler__overlay" aria-hidden="true"></div>
        <div id="vanilla-reader__ruler" aria-hidden="true">
            <div class="vanilla-reader__ruler__drag-handle" id="vanilla-reader__ruler__drag-handle-start"></div>
            <div id="vanilla-reader__ruler__linebox"></div>
            <div class="vanilla-reader__ruler__drag-handle" id="vanilla-reader__ruler__drag-handle-end"></div>
        </div>
    `;

    private _rulerElement: HTMLElement;
    private _rulerLineBoxElement: HTMLElement;
    private _rulerOverlayElement: HTMLElement;
    // private _rulerDragHandleStart: HTMLElement;
    // private _rulerDragHandleEnd: HTMLElement;

    private _uiFadeDuration: number = 200;
    private _isBeingDragged: boolean = false;
    private _initialPointerDownPosition: {x: number, y:number} | undefined;
    private _dragStartPositionOffset: ICoordinates | undefined;
    private _rulerLineBoxElementOffsetTop: number = (VanillaReaderAppUiDefaults.options.readingRulerOptions?.rulerBorderTopWidth || 0) + (VanillaReaderAppUiDefaults.options.readingRulerOptions?.lineBoxBorderTopWidth || 0);
    // @ts-ignore
    private _rulerLineBoxElementOffsetBottom: number = (VanillaReaderAppUiDefaults.options.readingRulerOptions?.rulerBorderBottomWidth || 0) + (VanillaReaderAppUiDefaults.options.readingRulerOptions?.lineBoxBorderBottomWidth || 0);
    private _rulerLineBoxMinHeight: number = VanillaReaderAppUiDefaults.options.readingRulerOptions!.lineBoxMinHeight!;
    //private _rulerLineBoxMaxHeight: number = VanillaReaderAppUiDefaults.options.readingRulerOptions!.lineBoxMaxHeight!;
    //private _rulerMaxHeight: number = 400;
    private _rulerLineBoxSpan: number = VanillaReaderAppUiDefaults.options.readingRulerOptions!.lineBoxLineSpan!;
    private _rulerDragThreshold: number = VanillaReaderAppUiDefaults.options.readingRulerOptions!.rulerDragThreshold!;
    private _rulerOpacity: number = VanillaReaderAppUiDefaults.options.readingRulerOptions!.rulerOpacity!;
    private _snapToContentLineBoxes: boolean = VanillaReaderAppUiDefaults.options.readingRulerOptions!.snapToLineBoxes!;

    private _contentLineBoxRects: IRect[] = [];
    private _contentLineBoxRectData: IVisibleContentRectData[] = [];
    private _currentIntersectingContentLineBoxRect: IRect | undefined = undefined;
    private _currentIntersectingContentLineBoxIndex: number | undefined = undefined;
    private _currentRulerLineBoxRect: DOMRect | undefined = undefined;
    private _contentLineBoxIntersectionMargin: number = 4;
    private _contentLineBoxMinWidthThreshold: number = 8;
    private _contentLineBoxAverageHeight: number = this._rulerLineBoxMinHeight;
    private _rulerLineBoxVerticalSpacingFactor: number = 1.8;
    private _animationDuration: number = 200;
    private _pageOverlayOpacity: number = 0.8;

    private _useReducedMotion: boolean = false;
    private _debugMode: boolean = false;

    private _activeOptions: IVanillaReaderUiReadingRulerOptions;

    private _onVisibleCallback: VanillaVoidCallback | undefined;
    private _onHiddenCallback: VanillaVoidCallback | undefined;
    private _onRulerClickCallback: VanillaMouseEventCallback | undefined;
    private _onRulerDragCallback: VanillaPointerEventCallback | undefined;
    private _onRulerDragStartCallback: VanillaPointerEventCallback | undefined;
    private _onRulerDragEndCallback: VanillaReadingRulerDragCallback | undefined;
    private _onRulerMoveEndCallback: VanillaReadingRulerMoveCallback | undefined;
    private _onRulerAtScreenTopCallback: VanillaPointerEventCallback | undefined;
    private _onRulerAtScreenBottomCallback: VanillaPointerEventCallback | undefined;

    constructor(
        private _hostElement: HTMLElement,
        insertPosition: InsertPosition = 'afterbegin', options?: IVanillaReaderUiReadingRulerOptions) {

        this._hostElement.insertAdjacentHTML(insertPosition, this._style);
        this._hostElement.insertAdjacentHTML(insertPosition, this._template);

        this._rulerElement = this._hostElement.querySelector('#vanilla-reader__ruler')!;
        this._rulerLineBoxElement = this._hostElement.querySelector('#vanilla-reader__ruler__linebox')!;
        this._rulerOverlayElement  = this._hostElement.querySelector('#vanilla-reader__ruler__overlay')!;
        // this._rulerDragHandleStart = this._hostElement.querySelector('#vanilla-reader__ruler__drag-handle-start')!;
        // this._rulerDragHandleEnd = this._hostElement.querySelector('#vanilla-reader__ruler__drag-handle-end')!;

        this._rulerElement.addEventListener('click', this._onRulerClick);

        this._rulerElement.addEventListener('pointerdown', this._onPointerDown);
        this._rulerElement.addEventListener('pointerup', this._onPointerUp);
        this._rulerElement.addEventListener('pointermove', this._onPointerMove);
        this._rulerElement.addEventListener('pointercancel', this._onPointerUp);

        this._rulerElement.addEventListener('transitionend', this._onTransitionEndOrCancel);
        this._rulerElement.addEventListener('transitioncancel', this._onTransitionEndOrCancel);

        this._activeOptions = this._applyOptions(options || {});

        this._toggleUiAnimations(this.useReducedMotion);

    }

    public get position(): ICoordinates {
        return {
            x: this._rulerElement.offsetLeft,
            y: this._rulerElement.offsetTop
        }
    }

    public get isOverlayVisible(): boolean {
        return this._rulerOverlayElement.style.visibility != 'hidden';
    }

    public get isVisible(): boolean {
        return this._rulerElement.style.display === 'flex';
    }

    public get isBeingDragged(): boolean {
        return this._isBeingDragged;
    }

    public get isAtStartOfContent(): boolean {
        return this._currentIntersectingContentLineBoxIndex !== undefined ? this._currentIntersectingContentLineBoxIndex === 0 : false;

    }

    public get isAtEndOfContent(): boolean {
        return this._currentIntersectingContentLineBoxIndex !== undefined ? this._currentIntersectingContentLineBoxIndex >= this._contentLineBoxRects.length - this._rulerLineBoxSpan : false;
    }

    public get rulerLineBoxBoundingBox(): DOMRect {
        let rect = this._rulerLineBoxElement.getBoundingClientRect();
        return new DOMRect(
            Math.floor(rect.x),
            Math.floor(rect.y),
            Math.floor(rect.width),
            Math.floor(rect.height));
    }

    public get rulerBoundingBox(): DOMRect {
        let rect = this._rulerElement.getBoundingClientRect();
        return new DOMRect(
            Math.floor(rect.x),
            Math.floor(rect.y),
            Math.floor(rect.width),
            Math.floor(rect.height));
    }

    public set useReducedMotion(state: boolean) {
        this._useReducedMotion = state;
        this._toggleUiAnimations(!state);
    }

    public get useReducedMotion(): boolean {
        return this._useReducedMotion;
    }

    public get snapToContentLineBoxes(): boolean {
        return this._snapToContentLineBoxes;
    }

    public set snapToContentLineBoxes(shouldSnap: boolean) {
        if(shouldSnap != this._snapToContentLineBoxes) {
            this.setOptions({
                snapToLineBoxes: shouldSnap
            });
        }
    }

    onClick(callback: VanillaMouseEventCallback): void {
        this._onRulerClickCallback = callback;
    }

    onDragEnd(callback: VanillaReadingRulerDragCallback): void {
        this._onRulerDragEndCallback = callback;
    }

    onDragStart(callback: VanillaPointerEventCallback): void {
        this._onRulerDragStartCallback = callback;
    }

    onDrag(callback: VanillaPointerEventCallback): void {
        this._onRulerDragCallback = callback;
    }

    onMoveEnd(callback: VanillaReadingRulerMoveCallback): void {
        this._onRulerMoveEndCallback = callback;
    }

    onAtScreenTop(callback: VanillaPointerEventCallback): void {
        this._onRulerAtScreenTopCallback = callback;
    }

    onAtScreenBottom(callback: VanillaPointerEventCallback): void {
        this._onRulerAtScreenBottomCallback = callback;
    }

    onVisible(callback: VanillaVoidCallback): void {
        this._onVisibleCallback = callback;
    }

    onHidden(callback: VanillaVoidCallback): void {
        this._onHiddenCallback = callback;
    }

    showPageOverlay(): void {
        this._rulerOverlayElement.style.visibility = 'visible';
    }

    hidePageOverlay(): void {
        this._rulerOverlayElement.style.visibility = 'hidden';
    }

    setOptions(options: IVanillaReaderUiReadingRulerOptions): IVanillaReaderUiReadingRulerOptions {
        return this._applyOptions(options);
    }

    setContentLineBoxes(rectData: IVisibleContentRectData[], _filterOutOverlappingLines: boolean = true): void {
        let processedRectData: IVisibleContentRectData[] = [];

        this._currentIntersectingContentLineBoxRect = undefined;
        this._currentIntersectingContentLineBoxIndex = undefined;

        processedRectData = this._filterOutBelowMinWidthLineBoxes(rectData);

        if(this._debugMode) {
            this._drawDebugContentLineBoxes(processedRectData);
        }

        let totalContentLineBoxHeight: number = this._rulerLineBoxMinHeight;

        processedRectData.forEach((rect: IVisibleContentRectData)=>{
           totalContentLineBoxHeight += rect.clientRect.height;
        });

        this._contentLineBoxAverageHeight = totalContentLineBoxHeight / processedRectData.length;

        this._contentLineBoxRectData = processedRectData;
        this._contentLineBoxRects = processedRectData.map(rectData => rectData.clientRect);
    }

    setRulerLineBoxHeight(heightRequestInPx: number, animate: boolean = true): DOMRect {

        // The useReducedMotion option always overrides any param value
        if(this.useReducedMotion) {
            animate = false;
        }

        this._toggleUiAnimations(animate);

        let newLineBoxHeight = Math.max(heightRequestInPx, this._rulerLineBoxMinHeight);

        let rulerLineBoxRect = new DOMRect(0, this._rulerLineBoxElementOffsetTop + this._rulerElement.offsetTop, this._rulerElement.clientWidth, newLineBoxHeight);

        this._rulerLineBoxElement.style.height = `${newLineBoxHeight}px`;

        this._moveAndScaleOverlayMaskRect(rulerLineBoxRect);

        return rulerLineBoxRect;

    }

    moveTo(y: number , animate: boolean = true, relativeToLineBox: boolean = false, affectReadingPosition: boolean = true): void {

        // There are many factors that can trigger the ruler to move, such as user interaction, but also
        // changes in reading position. In order to not react to redundant calls we check if we actually
        // need to move.
        if(!this._rulerNeedsToMove(y)) {
            return;
        }

        let rulerLineBoxElementOffsetTop = relativeToLineBox ? this._rulerLineBoxElementOffsetTop : 0;

        // The useReducedMotion option always overrides any param value
        if(this.useReducedMotion) {
            animate = false;
        }

        this._toggleUiAnimations(animate);

        let rulerLineBoxRect = this._currentRulerLineBoxRect || this.rulerLineBoxBoundingBox;

        let newRulerTop: number;

        if(this._dragStartPositionOffset) {
            // Take into account the drag offset set by the `rulerDragThreshold` option.
            newRulerTop = y - Math.floor((this._dragStartPositionOffset.y + rulerLineBoxElementOffsetTop));
            this._rulerElement.style.top = newRulerTop + 'px';
        } else {
            newRulerTop = Math.max(y - rulerLineBoxElementOffsetTop, 0);
            this._rulerElement.style.top = newRulerTop + 'px';
        }

        this._moveAndScaleOverlayMaskRect(new DOMRect(rulerLineBoxRect.x, newRulerTop + this._rulerLineBoxElementOffsetTop, rulerLineBoxRect.width, rulerLineBoxRect.height));

        if(!this.isBeingDragged) {
            // After both the line box animation and move animation have finished update the intersection data.
            // Also call the onMoveEnd callback.
            setTimeout(()=>{
                this._updateIntersectingContentLineBoxData();

                if(this._currentRulerLineBoxRect) {
                    this.setRulerLineBoxHeight(this._currentRulerLineBoxRect.height);
                }

                this._onMoveEnd(affectReadingPosition);
            }, this._animationDuration * 2);
        }

    }

    moveToAndAlign(y: number, contentLineBoxHeight?: number, animate: boolean = true, affectReadingPosition: boolean = true): void {
        let newLineBoxRect: DOMRect;

        // There are many factors that can trigger the ruler to move, such as user interaction, but also
        // changes in reading position. In order to not react to redundant calls we check if we actually
        // need to move.
        if(!this._rulerNeedsToMove(y)) {
            return;
        }

        // The useReducedMotion option always overrides any param value
        if(this.useReducedMotion) {
            animate = false;
        }

        this._toggleUiAnimations(animate);

        const getLineBoxHeightFunction = (): DOMRect => {
            let rulerLineBoxHeight;

            if(this._currentRulerLineBoxRect) {
                let currentIndex = this._currentIntersectingContentLineBoxIndex != undefined && this._currentIntersectingContentLineBoxIndex >= 0 ?
                    this._currentIntersectingContentLineBoxIndex: 0;
                let endIndex = currentIndex + this._rulerLineBoxSpan;
                let contentLineBoxSpanBoundingBox = this._getBoundingBoxForContentLineBoxSpan(this._contentLineBoxRects, currentIndex, endIndex);

                rulerLineBoxHeight = contentLineBoxSpanBoundingBox.height; //Math.min(contentLineBoxSpanHeight, this._rulerMaxHeight);
            } else {
                rulerLineBoxHeight = (contentLineBoxHeight ? contentLineBoxHeight : this._contentLineBoxAverageHeight) * this._rulerLineBoxSpan;
            }

            this._currentRulerLineBoxRect = this._getRulerLineBoxFromHeightRequest(rulerLineBoxHeight);

            return this._currentRulerLineBoxRect;
        };

        const moveToAndScaleFunction = (rulerLineBoxRect: DOMRect): DOMRect => {
            // let rulerRect = new DOMRect(
            //     rulerLineBoxRect.x,
            //     rulerLineBoxRect.y - this._rulerLineBoxElementOffsetTop,
            //     rulerLineBoxRect.width,
            //     this._rulerLineBoxElementOffsetTop + rulerLineBoxRect.height + this._rulerLineBoxElementOffsetBottom
            // );

            let newTopPosition = Math.floor(y - this._rulerLineBoxElementOffsetTop);
            this._rulerElement.style.top = `${newTopPosition}px`;

            this._rulerLineBoxElement.style.height = `${rulerLineBoxRect.height}px`;

            let overlayMaskRect = new DOMRect(
                rulerLineBoxRect.x,
                y,
                rulerLineBoxRect.width,
                rulerLineBoxRect.height);

            this._moveAndScaleOverlayMaskRect(overlayMaskRect);

            return  overlayMaskRect;
        };

        if(animate) {
            // After the move has completed, we scale the box to fit the configured no of line boxes
            newLineBoxRect = getLineBoxHeightFunction();
            // Move the ruler to its new top position.
            setTimeout(()=>{
                moveToAndScaleFunction(newLineBoxRect);
            }, this._animationDuration);
        } else {
            newLineBoxRect = getLineBoxHeightFunction();
            moveToAndScaleFunction(newLineBoxRect);
        }

        // After both the line box animation and move animation have finished update the intersection data.
        // Also call the onMoveEnd callback.
        setTimeout(()=>{
            this._updateIntersectingContentLineBoxData();
            this._onMoveEnd(affectReadingPosition);
        }, this._animationDuration * 2);

    }

    moveToNextContentLineBox(animate: boolean = true, affectReadingPosition: boolean = true): void {

        // The useReducedMotion option always overrides any param value
        if(this.useReducedMotion) {
            animate = false;
        }

        if(this._currentIntersectingContentLineBoxRect &&
            this._contentLineBoxRects &&
            this._currentIntersectingContentLineBoxIndex !== undefined &&
            this._currentIntersectingContentLineBoxIndex < this._contentLineBoxRects.length - 1) {

            let nextLineBox: IRect | undefined;
            let nextLineBoxIndex: number = Math.min(this._currentIntersectingContentLineBoxIndex + this._rulerLineBoxSpan, this._contentLineBoxRects.length);
            let intersectingLineBox = this._currentIntersectingContentLineBoxRect;

            for(let i = nextLineBoxIndex; this._contentLineBoxRects.length > i; i++) {
                let currentLineBox = this._contentLineBoxRects[i];
                if(currentLineBox.y >= (intersectingLineBox.y + intersectingLineBox.height) - this._contentLineBoxIntersectionMargin &&
                    !this._domRectsAreEquiv(intersectingLineBox, currentLineBox)) {
                    nextLineBox = currentLineBox;
                    nextLineBoxIndex = i;
                    break;
                }
            }
            if(nextLineBox) {
                this._updateIntersectingContentLineBoxData(nextLineBox, nextLineBoxIndex);
                if(this.snapToContentLineBoxes) {
                    this.moveToAndAlign(nextLineBox.y, nextLineBox.height, animate, affectReadingPosition);
                } else {
                    this.moveTo(nextLineBox.y, animate, true, affectReadingPosition);
                }

            } else {
                console.warn('VanillaReaderUiReadingRuler.moveToNextContentLineBox', 'Unable to find next linebox');
            }
        } else {
            console.warn('moveToNextContentLineBox', 'No _currentIntersectingContentLineBoxRect or _contentLineBoxRects', this._currentIntersectingContentLineBoxRect, this._contentLineBoxRects)
        }
    }

    moveToPreviousContentLineBox(animate: boolean = true, affectReadingPosition: boolean = true): void {

        // The useReducedMotion option always overrides any param value
        if(this.useReducedMotion) {
            animate = false;
        }

        if(this._currentIntersectingContentLineBoxRect &&
            this._contentLineBoxRects &&
            this._currentIntersectingContentLineBoxIndex !== undefined &&
            this._currentIntersectingContentLineBoxIndex > 0) {

            let previousLineBox: IRect | undefined;
            let previousLineBoxIndex: number = Math.max(this._currentIntersectingContentLineBoxIndex - this._rulerLineBoxSpan, 0);
            let intersectingLineBox = this._currentIntersectingContentLineBoxRect;

            for(let i = previousLineBoxIndex; i >= 0; i--) {
                let currentLineBox = this._contentLineBoxRects[i];
                if((currentLineBox.y + currentLineBox.height) <= intersectingLineBox.y + this._contentLineBoxIntersectionMargin  &&
                    !this._domRectsAreEquiv(intersectingLineBox, currentLineBox)) {
                    previousLineBox = currentLineBox;
                    break;
                }
            }
            if(previousLineBox) {
                this._updateIntersectingContentLineBoxData(previousLineBox, previousLineBoxIndex);

                if(this.snapToContentLineBoxes) {
                    this.moveToAndAlign(previousLineBox.y, previousLineBox.height, animate, affectReadingPosition);
                } else {
                    this.moveTo(previousLineBox.y, animate,true, affectReadingPosition);
                }
            } else {
                console.warn('VanillaReaderUiReadingRuler.moveToPreviousContentLineBox', 'Unable to find previous linebox');
            }
        } else {
            console.log('moveToPreviousContentLineBox', 'No _currentIntersectingContentLineBoxRect or _contentLineBoxRects', this._currentIntersectingContentLineBoxRect, this._contentLineBoxRects)
        }
    }

    moveToContentLineBoxIndex(index: number, animate: boolean = true, affectReadingPosition: boolean = true): void {
        // The useReducedMotion option always overrides any param value
        if(this.useReducedMotion) {
            animate = false;
        }

        if(this._contentLineBoxRects) {

            let lineBox: IRect | undefined = this._contentLineBoxRects[index];

            if(lineBox) {
                this._updateIntersectingContentLineBoxData(lineBox, index);
                this.moveToAndAlign(lineBox.y, lineBox.height, animate, affectReadingPosition);
            } else {
                console.warn('VanillaReaderUiReadingRuler.moveToContentLineBoxIndex', 'Unable to find lineBox with index', index);
            }
        } else {
            console.log('VanillaReaderUiReadingRuler.moveToContentLineBoxIndex', 'No _contentLineBoxRects')
        }
    }

    hide(): void {

        this._rulerOverlayElement.style.opacity = '0';
        this._rulerElement.style.opacity = '0';

        // Run after fade out
        setTimeout(()=>{
            this._rulerOverlayElement.style.display = 'none';
            this._rulerElement.style.display = 'none';
            if(this._onHiddenCallback) {
                this._onHiddenCallback();
            }
        },this._uiFadeDuration)

    }

    show(initialPosition?: number, affectReadingPosition: boolean = true): void {

        this._rulerElement.style.display = 'flex';
        this._rulerOverlayElement.style.display = 'flex';

        if(initialPosition) {
            this.moveToAndAlign(initialPosition, undefined, false, affectReadingPosition);
        } else {
            this._updateIntersectingContentLineBoxData();

            let lineBoxHeight: number = (this._rulerLineBoxMinHeight * this._rulerLineBoxVerticalSpacingFactor) * this._rulerLineBoxSpan;

            this._currentRulerLineBoxRect = this.setRulerLineBoxHeight(lineBoxHeight, false);
        }

        // Run after animations have finished
        setTimeout(()=>{

            this._rulerOverlayElement.style.opacity = this._pageOverlayOpacity.toString();
            this._rulerElement.style.opacity = this._rulerOpacity.toString();

            if(this._onVisibleCallback) {
                this._onVisibleCallback();
            }
        }, this._animationDuration);
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    _rulerNeedsToMove(y:number): boolean {
        let targetLineBoxYPos = Math.round(y);
        let currentLineBoxYPos = this._rulerElement.offsetTop + this._rulerLineBoxElementOffsetTop;

        return targetLineBoxYPos !== currentLineBoxYPos;
    }

    _getRulerLineBoxFromHeightRequest(heightRequestInPx: number): DOMRect {
        let newLineBoxHeight = Math.max(heightRequestInPx, this._rulerLineBoxMinHeight);
        let rulerLineBoxRect = new DOMRect(0, this._rulerLineBoxElementOffsetTop + this._rulerElement.offsetTop, this._rulerElement.clientWidth, newLineBoxHeight);

        return rulerLineBoxRect;

    }

    _redraw(): void {
        if(this._currentIntersectingContentLineBoxRect) {

            let rulerLineBoxHeight;
            let currentIndex = this._currentIntersectingContentLineBoxIndex != undefined && this._currentIntersectingContentLineBoxIndex >= 0 ?
                this._currentIntersectingContentLineBoxIndex: 0;
            let endIndex = currentIndex + this._rulerLineBoxSpan;

            let contentLineBoxSpanBoundingBox = this._getBoundingBoxForContentLineBoxSpan(this._contentLineBoxRects, currentIndex, endIndex);
            let averageLineBoxSpanHeight = (this._contentLineBoxAverageHeight * this._rulerLineBoxSpan);
            let contentLineBoxSpanHeight = contentLineBoxSpanBoundingBox?.height || averageLineBoxSpanHeight;
            rulerLineBoxHeight = contentLineBoxSpanHeight; // Math.min(contentLineBoxSpanHeight, this._rulerMaxHeight);

            this._currentRulerLineBoxRect = this.setRulerLineBoxHeight(rulerLineBoxHeight);
        }
    }

    _applyOptions(newOptions: IVanillaReaderUiReadingRulerOptions): IVanillaReaderUiReadingRulerOptions {
        let defaultReadingRulerOptions: IVanillaReaderUiReadingRulerOptions = {... VanillaReaderAppUiDefaults.options.readingRulerOptions};

        for (let key in newOptions) {
            if ((newOptions as {[key: string]: number})[key] === undefined) {
                delete (newOptions as {[key: string]: number})[key];
            }
        }

        let newOptionsAppliedToDefaults = Object.assign(defaultReadingRulerOptions!, newOptions);
        let normalizedOptions = Object.assign(this._activeOptions || {}, newOptionsAppliedToDefaults);

        this._rulerElement.style.backgroundColor = normalizedOptions.rulerBackgroundColor!;
        this._rulerElement.style.mixBlendMode = normalizedOptions.rulerMixBlendMode!;
        this._rulerElement.style.opacity = normalizedOptions.rulerOpacity!.toString(2);
        //this._rulerElement.style.minHeight = `${normalizedOptions.rulerMinHeight}px`;

        this._rulerOverlayElement.style.backgroundColor = normalizedOptions.overlayBackgroundColor!;
        this._rulerOverlayElement.style.mixBlendMode = normalizedOptions.overlayMixBlendMode!;
        this._rulerOverlayElement.style.opacity = `${normalizedOptions.overlayOpacity!}`

        this._rulerLineBoxElement.style.backgroundColor = normalizedOptions.lineBoxBackgroundColor!;
        this._rulerLineBoxElement.style.mixBlendMode = normalizedOptions.lineBoxMixBlendMode!;
        this._rulerLineBoxElement.style.opacity = normalizedOptions.lineBoxOpacity!.toString(2);

        this._rulerLineBoxElement.style.borderStyle = 'solid';
        this._rulerLineBoxElement.style.borderTopWidth = `${normalizedOptions.lineBoxBorderTopWidth}px`;
        this._rulerLineBoxElement.style.borderBottomWidth = `${normalizedOptions.lineBoxBorderBottomWidth}px`;
        this._rulerLineBoxElement.style.borderLeftWidth = `${normalizedOptions.lineBoxBorderLeftWidth}px`;
        this._rulerLineBoxElement.style.borderRightWidth = `${normalizedOptions.lineBoxBorderRightWidth}px`;
        this._rulerLineBoxElement.style.borderTopColor = `${normalizedOptions.lineBoxBorderTopColor}`;
        this._rulerLineBoxElement.style.borderBottomColor = `${normalizedOptions.lineBoxBorderBottomColor}`;
        this._rulerLineBoxElement.style.borderLeftColor = `${normalizedOptions.lineBoxBorderLeftColor}`;
        this._rulerLineBoxElement.style.borderRightColor = `${normalizedOptions.lineBoxBorderRightColor}`;

        this._rulerElement.style.borderStyle = 'solid';
        this._rulerElement.style.borderTopWidth = `${normalizedOptions.rulerBorderTopWidth}px`;
        this._rulerElement.style.borderTopColor = `${normalizedOptions.rulerBorderTopColor}`;
        this._rulerElement.style.borderBottomWidth = `${normalizedOptions.rulerBorderBottomWidth}px`;
        this._rulerElement.style.borderBottomColor = `${normalizedOptions.rulerBorderBottomColor}`;
        this._rulerElement.style.borderLeftWidth = `${normalizedOptions.rulerBorderLeftWidth}px`;
        this._rulerElement.style.borderLeftColor = `${normalizedOptions.rulerBorderLeftColor}`;
        this._rulerElement.style.borderRightWidth = `${normalizedOptions.rulerBorderRightWidth}px`;
        this._rulerElement.style.borderRightColor = `${normalizedOptions.rulerBorderRightColor}`;

        this._rulerLineBoxElement.style.minHeight = `${normalizedOptions.lineBoxMinHeight}px`;

        this._rulerDragThreshold = normalizedOptions.rulerDragThreshold!;
        this._rulerLineBoxMinHeight = normalizedOptions.lineBoxMinHeight!;
        this._rulerOpacity = normalizedOptions.rulerOpacity!;
        this._snapToContentLineBoxes = normalizedOptions.snapToLineBoxes!;
        this._rulerLineBoxSpan = normalizedOptions.lineBoxLineSpan!;
        this._rulerLineBoxVerticalSpacingFactor = normalizedOptions.lineBoxLineVerticalSpacing!;
        this._pageOverlayOpacity = normalizedOptions.overlayOpacity!;

        if(normalizedOptions.overlayIsVisible) {
            this.showPageOverlay();
        } else {
            this.hidePageOverlay();
        }

        this._redraw();

        this._rulerLineBoxElementOffsetTop = (normalizedOptions.rulerBorderTopWidth || 0) + (normalizedOptions.lineBoxBorderTopWidth || 0);
        this._rulerLineBoxElementOffsetBottom = (normalizedOptions.rulerBorderBottomWidth || 0) + (normalizedOptions.lineBoxBorderBottomWidth || 0);

        this._activeOptions = normalizedOptions;

        return normalizedOptions;
    }

    _getBoundingBoxForContentLineBoxSpan(contentLineBoxRects: IRect[], startIndex: number, endIndex: number): DOMRect {

        let minX: number = Infinity;
        let maxX: number = 0;
        let minY: number = Infinity;
        let maxY: number = 0;

        let startEndDelta: number = Math.abs(endIndex - startIndex);

        startIndex = Math.max(startIndex, 0);
        endIndex = startIndex + startEndDelta < contentLineBoxRects.length ? startIndex + startEndDelta : contentLineBoxRects.length;

        for (let i = startIndex; i < endIndex; i++){
            const rect = contentLineBoxRects[i];
            let rectBottom = rect.y + rect.height;

            minX = rect.x < minX ? rect.x : minX;
            maxX = rect.x + rect.width > maxX ? rect.x + rect.width : maxX;
            minY = rect.y < minY ? rect.y : minY;
            maxY = rectBottom > maxY ? rectBottom : maxY;
        }

        return  new DOMRect(minX, minY, maxX - minX, maxY - minY);

    }

    _filterOutBelowMinWidthLineBoxes(rectData: IVisibleContentRectData[]): IVisibleContentRectData[] {
        return rectData.filter((rect: IVisibleContentRectData)=>{
            return rect.clientRect.width >= this._contentLineBoxMinWidthThreshold;
        });
    }

    _filterOutOverlappingLines(rects: DOMRect[]): DOMRect[] {
        let processedRects: DOMRect[] = [];
        let previousRect: DOMRect | undefined;

        for (let i = 0; rects.length > i; i++) {
            if(previousRect) {
                let overlapPreviousBottom = Math.max(previousRect.bottom - rects[i].top, 0);

                if((rects[i].top > previousRect.top && rects[i].top < previousRect.bottom) && rects[i].height > previousRect.height &&
                        overlapPreviousBottom > this._contentLineBoxIntersectionMargin) {
                    console.log('VanillaReaderUiReadingRuler.setContentLineBoxes: rect ', rects[i], ' overlaps ', previousRect);
                } else {
                    processedRects.push(rects[i]);
                }
            } else {
                processedRects.push(rects[i]);
            }
            previousRect = rects[i];
        }

        return processedRects;
    }

    _moveAndScaleOverlayMaskRect(rect: DOMRect): void {
        this._rulerOverlayElement.style.setProperty('--vanilla-reader__ruler__overlay__ruler-clip-box-left', `${rect.left}px`);
        this._rulerOverlayElement.style.setProperty('--vanilla-reader__ruler__overlay__ruler-clip-box-top', `${rect.top + 1}px`);
        this._rulerOverlayElement.style.setProperty('--vanilla-reader__ruler__overlay__ruler-clip-box-right', `${rect.right}px`);
        this._rulerOverlayElement.style.setProperty('--vanilla-reader__ruler__overlay__ruler-clip-box-bottom', `${rect.bottom - 1}px`);
    }

    _updateIntersectingContentLineBoxData(forceUpdateRect?: IRect, forceUpdateIndex?: number): IRect | undefined {

        if(forceUpdateRect || forceUpdateIndex) {
            this._currentIntersectingContentLineBoxRect = forceUpdateRect;
            this._currentIntersectingContentLineBoxIndex = forceUpdateIndex;
            return;
        }

        if(this._contentLineBoxRects) {
            let intersectingRect = this._contentLineBoxRects?.find((rect: IRect, index: number)=>{

                let rectTop = rect.y;
                let rectBottom = rect.y + rect.height;

                let topIntersects: boolean = rectTop <= this.rulerLineBoxBoundingBox.bottom &&
                    rect.y >= this.rulerLineBoxBoundingBox.top - this._contentLineBoxIntersectionMargin;

                let bottomIntersects: boolean = rectBottom >= this.rulerLineBoxBoundingBox.top &&
                    rectBottom <= this.rulerLineBoxBoundingBox.bottom + this._contentLineBoxIntersectionMargin;

                let contains: boolean = rectTop < this.rulerLineBoxBoundingBox.top && rectBottom > this.rulerLineBoxBoundingBox.bottom;

                let doesIntersect = topIntersects || bottomIntersects || contains;

                if(doesIntersect) {
                    this._currentIntersectingContentLineBoxRect = rect;
                    this._currentIntersectingContentLineBoxIndex = index;
                }

                return doesIntersect;

            });

            return intersectingRect || undefined;
        }else {
            return;
        }

    }

    _onRulerClick = (ev: MouseEvent) => {
        if(this._onRulerClickCallback && this._initialPointerDownPosition && Math.abs(this._initialPointerDownPosition.y - ev.clientY) < 2) {
            this._onRulerClickCallback(ev);
        }

        this._initialPointerDownPosition = undefined;

    }

    _onPointerDown = (ev: PointerEvent) => {
        this._initialPointerDownPosition = {
            x: ev.clientX,
            y: ev.clientY,
        };

        try {
            this._rulerElement.setPointerCapture(ev.pointerId);
        } catch (e) {

        }

    }

    _onPointerUp = (ev: PointerEvent) => {

        // Update the intersection data based on the new ruler position
        this._updateIntersectingContentLineBoxData();

        if(this.isBeingDragged) {
            this._onDragEnd(ev);
        }

        try {
            this._rulerElement.releasePointerCapture(ev.pointerId);
        } catch (e) {}

        if(this._initialPointerDownPosition && Math.abs(this._initialPointerDownPosition.y - ev.clientY) > 2) {
            ev.preventDefault();
            ev.stopPropagation();
        }

    }

    _onPointerMove = (ev: PointerEvent) => {

        if(!(ev.buttons & 1) || !this._initialPointerDownPosition || Math.abs(ev.clientY - this._initialPointerDownPosition.y) < this._rulerDragThreshold) {
            return;
        } else {
            if(this.isBeingDragged) {
                this._onDrag(ev);
            } else {
                this._onDragStart(ev);
            }
        }
    }

    _onDragStart = (ev: PointerEvent) => {

        this._isBeingDragged = true;

        this._currentIntersectingContentLineBoxRect = undefined;
        this._currentIntersectingContentLineBoxIndex = undefined;
        let rulerElementClientRect: DOMRect = this.rulerBoundingBox;

        let newLineHeight = Math.min(
            (
                (this._contentLineBoxAverageHeight * this._rulerLineBoxVerticalSpacingFactor) *
                this._rulerLineBoxSpan
            ), (this._rulerLineBoxMinHeight * this._rulerLineBoxSpan) * this._rulerLineBoxVerticalSpacingFactor
        );

        this._currentRulerLineBoxRect = this.setRulerLineBoxHeight(newLineHeight, false);

        this._dragStartPositionOffset = {
            x: ev.clientX - rulerElementClientRect.left,
            y: ev.clientY - rulerElementClientRect.top
        }

        this.moveTo(ev.clientY, false);

        this._rulerElement.classList.add('isBeingDragged');

        if(this._onRulerDragStartCallback) {
            this._onRulerDragStartCallback(ev);
        }
    }

    _onDrag = (ev: PointerEvent) => {

        if(!this.isBeingDragged || !this._dragStartPositionOffset) {
            return;
        }

        ev.preventDefault();
        ev.stopPropagation();

        if(this._onRulerDragCallback) {
            this._onRulerDragCallback(ev);
        }

        this.moveTo(ev.clientY, false);

        let rulerElementClientRect: DOMRect = this.rulerBoundingBox;
        if(rulerElementClientRect.bottom >= window.innerHeight){
            if(this._onRulerAtScreenBottomCallback) {
                this._onRulerAtScreenBottomCallback(ev);
            }
        }

        if(rulerElementClientRect.top <= 0){
            if(this._onRulerAtScreenTopCallback) {
                this._onRulerAtScreenTopCallback(ev);
            }
        }
    }

    _onDragEnd = (ev: PointerEvent) => {

        if(this._snapToContentLineBoxes) {
            if(this._currentIntersectingContentLineBoxRect) {
                this.moveToAndAlign(this._currentIntersectingContentLineBoxRect.y, this._currentIntersectingContentLineBoxRect.height, !this.useReducedMotion);
            }
        }

        if(this._onRulerDragEndCallback && this._currentIntersectingContentLineBoxIndex) {
            this._onRulerDragEndCallback(ev, this._contentLineBoxRectData[this._currentIntersectingContentLineBoxIndex]);
        }

        this._rulerElement.classList.remove('isBeingDragged');
        this._isBeingDragged = false;
        this._dragStartPositionOffset = undefined;
    }

    _onTransitionEndOrCancel = (_ev: TransitionEvent) => {

        this._updateIntersectingContentLineBoxData();
    }

    _onMoveEnd(affectReadingPosition: boolean = true): void {
        if(this._onRulerMoveEndCallback) {
            if(this._currentIntersectingContentLineBoxIndex !== undefined) {
                this._onRulerMoveEndCallback(this._contentLineBoxRectData[this._currentIntersectingContentLineBoxIndex], affectReadingPosition);
            }
        }
    }

    _toggleUiAnimations(animate: boolean): void {
        if(animate) {
            this._rulerElement.style.transition = `top ${this._animationDuration}ms ease 0s, height ${this._animationDuration}ms ease 0s, opacity ${this._uiFadeDuration}ms ease 0s`;
            this._rulerLineBoxElement.style.transition = `top ${this._animationDuration}ms ease 0s, height ${this._animationDuration}ms ease 0s, opacity ${this._uiFadeDuration}ms ease 0s`;
            this._rulerOverlayElement.style.transition = `clip-path ${this._animationDuration}ms ease 0s, opacity ${this._uiFadeDuration}ms ease 0s`;
        } else {
            this._rulerElement.style.transition = `opacity ${this._uiFadeDuration}ms ease 0s`;
            this._rulerLineBoxElement.style.transition = `opacity ${this._uiFadeDuration}ms ease 0s`;
            this._rulerOverlayElement.style.transition = `opacity ${this._uiFadeDuration}ms ease 0s`;
        }

    }

    _isValidOverlap(rect: DOMRect, overlappingRect: DOMRect): boolean {
        let overlap = Math.abs(rect.bottom - overlappingRect.top);
        let maxValidOverlap = rect.height * this._contentLineBoxIntersectionMargin;
        return overlap > maxValidOverlap;
    }

    _domRectsAreEquiv(rect1: IRect, rect2: IRect): boolean {
        return rect1.y === rect2.y && rect1.x === rect2.x && rect1.height === rect2.height && rect1.width === rect2.width;
    }

    _drawDebugContentLineBoxes(rectData: IVisibleContentRectData[]): void {
        document.querySelectorAll('.debug-rect').forEach((el: Element)=>{
            el.remove();
        });

        rectData.forEach((data)=>{
            let rect = data.clientRect;
            let rectSelector = data.locator.selectors[0];
            let rectDiv = document.createElement('div');
            rectDiv.className = 'debug-rect';
            rectDiv.style.position = 'absolute';
            rectDiv.style.border = '1px solid green';
            rectDiv.style.mixBlendMode = 'multiply';
            rectDiv.style.width = `${rect.width}px`;
            rectDiv.style.height = `${rect.height}px`;
            rectDiv.style.top = `${rect.y}px`;
            rectDiv.style.left = `${rect.x}px`;
            rectDiv.style.zIndex = '10';
            rectDiv.style.pointerEvents = 'none';
            rectDiv.title = `x: ${rect.x}, y: ${rect.y}, width: ${rect.width}, height: ${rect.height}, top: ${rect.y}, bottom: ${rect.y + rect.height}, selector: ${rectSelector}`;

            document.body.appendChild(rectDiv);

        });
    }
}
